<template>
  <vs-wrapper space="small">
    <vs-autocomplete
      data-test="resource-assignment"
      label="Assign To"
      :items="assignmentOptions"
      :value="assignmentValue"
      @input="updateAssignment"
    />
  </vs-wrapper>
</template>

<script setup lang="ts">
import { orderBy, uniq } from 'lodash'
import { computed, ref } from 'vue'
import { useStore } from 'vuex-composition-helpers'
import Company from '@/models/company'
import { BusinessUnit } from '@/models/business-unit'
import { UnitNumber } from '@/models/unit_number'

type AssignmentOption = {
  header?: string
  text?: string
  value?: string
  icon?: string
  description?: string
}

const store = useStore()

const getAssignableResources = computed<(hubId: string, resourceTypeId: number) => UnitNumber[]>(
  () => store.getters['unitNumbers/getAssignableUnitsForBusinessUnitAndResourceType'],
)

const getBusinessUnitById = computed<(id: string) => BusinessUnit>(
  () => store.getters['businessUnits/getBusinessUnitById'],
)

const getDispatchersOrAdminsInOrganization = computed<(id: string) => boolean>(
  () => store.getters['users/getDispatchersOrAdminsInOrganization'],
)

const getOrganizationDetails = computed<(id: string) => Company>(
  () => store.getters['companies/getByOrganizationId'],
)

const getUserOrgId = computed<string>(() => store.getters['permissions/getOrganizationId'])

const emit = defineEmits<{ (event: 'input', value: string) }>()

const props = defineProps<{
  hubId: string
  resourceTypeId: number
}>()

const assignmentValue = ref('')

const assignableResources = computed<UnitNumber[]>(() =>
  getAssignableResources.value(props.hubId, props.resourceTypeId),
)

const organizationOptions = computed(() => {
  const organizationIds = uniq(assignableResources.value.map((r) => r.organizationId))
  const organizations = organizationIds.map((id) => {
    return getBusinessUnitById.value(id)
  })
  return orderBy(organizations, [(o) => o.name.toLowerCase()])
})

const assignmentOptions = computed<AssignmentOption[]>(() => {
  const options: AssignmentOption[] = []

  organizationOptions.value.forEach((org) => {
    options.push({ header: org.name })

    const orgDetails = getOrganizationDetails.value(org.id)

    if (org.id !== getUserOrgId.value && getDispatchersOrAdminsInOrganization.value(org.id)) {
      options.push({
        text: org.name,
        value: `org__${org.id}`,
        description: organizationDescription(orgDetails.email),
        icon: 'villa',
      })
    }

    const resourcesForOrganization = assignableResources.value.filter(
      (r) => r.organizationId === org.id,
    )
    const orderedResources = orderBy(resourcesForOrganization, [
      'isOffline',
      (r) => r.name.toLowerCase(),
    ])

    options.push(
      ...orderedResources.map((r) => ({
        text: r.name,
        value: `resource__${r.id}`,
        description: resourceDescription(orgDetails.shortCode, r.description),
        ...(r.status === 0 && { chip: 'Offline' }),
      })),
    )
  })

  return options
})

const organizationDescription = (email) => {
  const message = `Let their dispatchers assign their own resource.\n`
  if (email) {
    return message + `We'll notify ${email}`
  }
  return message + `No email setup to notify this company yet.`
}
const resourceDescription = (shortcode, description) => {
  return description ? `${shortcode} · ${description}` : shortcode
}

const updateAssignment = (value) => {
  assignmentValue.value = value
  emit('input', value)
}
</script>
